import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar } from '@mui/material';
import { renewLicense } from '../api/admin';

interface RenewLicenseDialogProps {
    open: boolean;
    onClose: () => void;
    license: any;
    onSuccess: (newExpiry: number) => void;
}

export function RenewLicenseDialog({ open, onClose, license, onSuccess }: RenewLicenseDialogProps) {
    const [renewDuration, setRenewDuration] = useState('30_days');
    const [customExpiryDate, setCustomExpiryDate] = useState('');
    const [renewNotes, setRenewNotes] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    // Reset fields on modal open/close
    useEffect(() => {
        if (open) {
            setRenewDuration('30_days');
            setCustomExpiryDate('');
            setRenewNotes('');
        }
    }, [open]);

    const renewMutation = useMutation({
        mutationFn: ({ licenseKey, duration, customExpiry, notes }: { licenseKey: string; duration?: string; customExpiry?: number; notes?: string }) =>
            renewLicense(licenseKey, { duration, customExpiry, notes }),
        onSuccess: (resData: any) => {
            onSuccess(resData.newExpiry);
        },
        onError: (err: any) => {
            showMessage(err.response?.data?.error || 'Renewal failed');
        }
    });

    const handleRenewSubmit = () => {
        if (!license) return;

        const payload: any = {
            licenseKey: license.licenseKey,
            notes: renewNotes.trim()
        };

        if (renewDuration === 'custom') {
            if (!customExpiryDate) {
                showMessage('Please pick an expiry date');
                return;
            }
            payload.customExpiry = new Date(customExpiryDate).getTime();
        } else {
            payload.duration = renewDuration;
        }

        renewMutation.mutate(payload);
    };

    const handleClose = () => {
        if (renewMutation.isPending) return; // Prevent closing while mutation runs
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{ className: 'w-[400px] max-w-full !rounded-3xl dark:bg-gray-800 dark:text-white p-2' }}
            >
                <DialogTitle className="!font-bold !text-lg text-gray-900 dark:text-white">Renew License</DialogTitle>
                <DialogContent className="space-y-4 !py-2">
                    {license && (
                        <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-2xl border dark:border-gray-700 text-xs">
                            <p className="font-semibold text-gray-500">License Key</p>
                            <p className="font-mono text-gray-800 dark:text-gray-200 font-bold break-all">{license.licenseKey}</p>
                            <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-200/60 dark:border-gray-700/60">
                                <div>
                                    <p className="text-gray-400 font-medium">Product</p>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">{license.productId}</p>
                                </div>
                                <div>
                                    <p className="text-gray-400 font-medium">Current Expiry</p>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300 font-mono">
                                        {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Select Expiry Option</label>
                        <select
                            value={renewDuration}
                            onChange={(e) => setRenewDuration(e.target.value)}
                            disabled={renewMutation.isPending}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option value="30_days">+30 Days</option>
                            <option value="90_days">+90 Days</option>
                            <option value="180_days">+180 Days</option>
                            <option value="1_year">+1 Year</option>
                            <option value="custom">Exact Custom Date</option>
                        </select>
                    </div>

                    {renewDuration === 'custom' && (
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Pick Expiry Date</label>
                            <input
                                type="date"
                                value={customExpiryDate}
                                onChange={(e) => setCustomExpiryDate(e.target.value)}
                                disabled={renewMutation.isPending}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Renewal Notes (Optional)</label>
                        <textarea
                            value={renewNotes}
                            onChange={(e) => setRenewNotes(e.target.value)}
                            disabled={renewMutation.isPending}
                            placeholder="e.g. Annual contract extension"
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>
                </DialogContent>
                <DialogActions className="!px-6 !pb-4">
                    <Button onClick={handleClose} disabled={renewMutation.isPending} className="!text-gray-500 hover:!bg-slate-100">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRenewSubmit}
                        variant="contained"
                        color="primary"
                        disabled={renewMutation.isPending}
                        className="!rounded-lg !px-4"
                    >
                        {renewMutation.isPending ? 'Renewing...' : 'Renew License'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error notifications inside the dialog */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </>
    );
}
